/**
 * What a visitor sees when the tenant closed „vaše adresa u nás".
 *
 * The gate lives on the API (``core/platform_address_middleware.py``),
 * which refuses the customer surface with ``platform_address_hidden``
 * (404) or ``platform_address_login_required`` (401). Without this
 * screen the SPA would keep rendering its ordinary chrome around a page
 * whose every request failed — a shop that looks broken rather than
 * closed, including to the tenant's own staff.
 *
 * 🔑 Deliberately driven by the API's refusal rather than by a route
 * allow-list of its own. The server already decides which paths survive
 * a closed address (sign-in, the admin, order tracking, a quote link
 * somebody holds); a second list in the router would be the same rule
 * with two writers, and the FE copy would be the one that rots — it is
 * the one no test of the gate can reach.
 *
 * Calm, not alarming. „Skryté" is a state the tenant chose, so this is
 * not an error page: no red, no support plea. The sign-in variant's job
 * is to hand over a door.
 *
 * 🚫 **Not an overlay, and not ``IngotModal``.** It REPLACES the route
 * tree (see ``PaywallProvider``) rather than covering it, which is both
 * the better answer and the honest one:
 *
 * - there is nothing worth seeing underneath — every request behind it
 *   was refused, so an overlay would be a lid on a broken page;
 * - the Ingot dialog shell requires an ``onClose`` and wires ESC and
 *   backdrop-click to it. A refusal with a dismiss gesture is a lie: it
 *   would drop the visitor onto the wreckage it was hiding.
 *
 * Which is why it hand-rolls no ``fixed inset-0`` either — the
 * ``ingot-overlays`` ratchet counts those, and this screen genuinely is
 * not one.
 */

import { useTranslation } from "react-i18next";

import { DEFAULT_LANG } from "@/i18n";

export type PlatformAddressRefusal = "hidden" | "login_required";

/**
 * Recognise the gate's refusal on an ``ApiError``.
 *
 * Matches on ``code`` rather than the ``X-Platform-Address`` header for
 * the same reason ``tenantStatusFromError`` does: ``ApiError`` carries
 * the parsed body, not the headers. The header is there for anything
 * outside the browser.
 */
export function platformAddressFromError(err: {
  status: number;
  code: string;
}): PlatformAddressRefusal | null {
  if (err.status === 404 && err.code === "platform_address_hidden") {
    return "hidden";
  }
  if (err.status === 401 && err.code === "platform_address_login_required") {
    return "login_required";
  }
  return null;
}

/**
 * The language segment of the current URL.
 *
 * This renders OUTSIDE the router (``PaywallProvider`` wraps
 * ``RouterProvider``), so ``useParams`` is not available and the link
 * has to be a plain anchor. A shape check is enough — the worst case of
 * guessing wrong is a sign-in page in the default language.
 */
function langFromPath(pathname: string): string {
  const first = pathname.split("/")[1] ?? "";
  return /^[a-z]{2,8}$/.test(first) ? first : DEFAULT_LANG;
}

export function PlatformAddressNotice({
  refusal,
}: {
  refusal: PlatformAddressRefusal;
}): JSX.Element {
  const { t } = useTranslation("common");
  const lang = langFromPath(
    typeof window === "undefined" ? "" : window.location.pathname,
  );

  return (
    <div
      data-testid="platform-address-notice"
      data-refusal={refusal}
      className="flex min-h-screen items-center justify-center bg-bg p-6"
    >
      <div className="max-w-md rounded-lg border border-border bg-surface p-8 text-center shadow-xl">
        <h1 className="mb-2 text-xl font-semibold text-ink">
          {t(`platformAddress.${refusal}.heading`)}
        </h1>
        <p className="text-sm text-ink-2">
          {t(`platformAddress.${refusal}.body`)}
        </p>
        {refusal === "login_required" && (
          <a
            href={`/${lang}/sign-in`}
            className="mt-5 inline-block rounded-md bg-ink px-4 py-2 text-sm font-medium text-bg"
            data-testid="platform-address-sign-in"
          >
            {t("platformAddress.login_required.action")}
          </a>
        )}
      </div>
    </div>
  );
}
